// src/components/Groups/BulkShareModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Share2, FlaskConical, BarChart2, Image, Layers, AlertTriangle, Settings } from 'lucide-react';
import { groupsAPI } from '../../api/groupsApi';

const RESOURCE_TYPES = [
  { value: 'experiment', label: 'Experiments', icon: <FlaskConical size={16} /> },
  { value: 'analysis', label: 'Analyses', icon: <BarChart2 size={16} /> },
  { value: 'visualization', label: 'Visualizations', icon: <Image size={16} /> },
  { value: 'photo_job', label: 'Photo Jobs', icon: <Image size={16} /> },
];

const PERMISSION_LEVELS = [
  { value: 'view', label: 'View Only', description: 'Members can view but not modify' },
  { value: 'edit', label: 'View & Edit', description: 'Members can view and modify' },
  { value: 'admin', label: 'Full Control', description: 'Members can manage and share further' },
];

const BulkShareModal = ({ show, onHide, group, onShared }) => {
  const [selectedTypes, setSelectedTypes] = useState(['experiment']);
  const [permissionLevel, setPermissionLevel] = useState('view');
  const [enableAutoShare, setEnableAutoShare] = useState(false);
  const [autoSharePermission, setAutoSharePermission] = useState('view');
  const [autoShareResourceTypes, setAutoShareResourceTypes] = useState(['experiment']);
  const [sharingInProgress, setSharingInProgress] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [shareResult, setShareResult] = useState(null);
  const [autoShareStatus, setAutoShareStatus] = useState(null);

  // Fetch current auto-share status when modal opens
  useEffect(() => {
    if (show && group?.id) {
      fetchAutoShareStatus();
    }
  }, [show, group?.id]);

  const fetchAutoShareStatus = async () => {
    try {
      const data = await groupsAPI.getAutoShareStatus(group.id);
      if (data.auto_share_enabled) {
        setEnableAutoShare(true);
        setAutoSharePermission(data.permission_level);
        setAutoShareResourceTypes(data.resource_types);
        setAutoShareStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch auto-share status:', err);
    }
  };

  const handleTypeToggle = (type, isAutoShare = false) => {
    if (isAutoShare) {
      setAutoShareResourceTypes(prev =>
        prev.includes(type)
          ? prev.filter(t => t !== type)
          : [...prev, type]
      );
    } else {
      setSelectedTypes(prev =>
        prev.includes(type)
          ? prev.filter(t => t !== type)
          : [...prev, type]
      );
    }
  };

  const handleShare = async () => {
    if (selectedTypes.length === 0) {
      setError('Please select at least one resource type to share');
      return;
    }

    try {
      setSharingInProgress(true);
      setError(null);
      setShareResult(null);

      const payload = {
        resource_types: selectedTypes,
        permission_level: permissionLevel,
        enable_auto_share: enableAutoShare,
      };

      if (enableAutoShare) {
        payload.auto_share_permission = autoSharePermission;
        payload.auto_share_resource_types = autoShareResourceTypes;
      }

      const result = await groupsAPI.shareMultipleResources(group.id, payload);

      setShareResult(result);

      let message = `Successfully shared ${result.total_shared} resource(s) with ${group.name}`;
      if (result.auto_share?.enabled) {
        message += `. Auto-sharing enabled for ${result.auto_share.resource_types.join(', ')}`;
      }
      setSuccess(message);

      if (onShared) {
        onShared(result);
      }

      // Don't auto-close if there were failures, let user see the details
      if (result.failed_count === 0) {
        setTimeout(() => {
          onHide();
          resetForm();
        }, 2500);
      }
    } catch (err) {
      console.error('Error sharing resources:', err);
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to share resources');
    } finally {
      setSharingInProgress(false);
    }
  };

  const resetForm = () => {
    setSelectedTypes(['experiment']);
    setPermissionLevel('view');
    setEnableAutoShare(false);
    setAutoSharePermission('view');
    setAutoShareResourceTypes(['experiment']);
    setShareResult(null);
    setSuccess(null);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <Layers size={18} className="me-2" />
          Bulk Share with {group?.name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
            <div className="d-flex align-items-center">
              <span>{success}</span>
            </div>
          </Alert>
        )}

        {shareResult && (
          <div className="mb-3">
            <h6 className="mb-2">Share Results:</h6>
            {Object.entries(shareResult.results).map(([type, result]) => (
              <div key={type} className="d-flex align-items-center mb-1 p-2 bg-light rounded">
                {RESOURCE_TYPES.find(t => t.value === type)?.icon}
                <span className="ms-2 text-capitalize">{type}:</span>
                <span className="ms-2 badge bg-success">{result.shared_count} shared</span>
                {result.failed_count > 0 && (
                  <span className="ms-1 badge bg-warning">{result.failed_count} failed</span>
                )}
              </div>
            ))}
            {shareResult.auto_share?.enabled && (
              <div className="mt-2 p-2 bg-info bg-opacity-10 rounded">
                <Settings size={14} className="me-1" />
                <small className="text-info">
                  {shareResult.auto_share.message}
                </small>
              </div>
            )}
          </div>
        )}

        {/* Resource Type Selection */}
        <Form.Group className="mb-3">
          <Form.Label><strong>Resource Types to Share</strong></Form.Label>
          <div className="d-flex flex-wrap gap-2">
            {RESOURCE_TYPES.map(type => (
              <Button
                key={type.value}
                size="sm"
                variant={selectedTypes.includes(type.value) ? 'primary' : 'outline-secondary'}
                onClick={() => handleTypeToggle(type.value)}
                disabled={sharingInProgress}
                className="d-flex align-items-center"
              >
                {type.icon}
                <span className="ms-1">{type.label}</span>
              </Button>
            ))}
          </div>
          <Form.Text className="text-muted">
            Select one or more resource types to share with this group
          </Form.Text>
        </Form.Group>

        {/* Permission Level */}
        <Form.Group className="mb-4">
          <Form.Label><strong>Permission Level</strong></Form.Label>
          <div className="d-flex gap-2">
            {PERMISSION_LEVELS.map(perm => (
              <Button
                key={perm.value}
                size="sm"
                variant={permissionLevel === perm.value ? 'primary' : 'outline-secondary'}
                onClick={() => setPermissionLevel(perm.value)}
                disabled={sharingInProgress}
                className="flex-grow-1"
                title={perm.description}
              >
                {perm.label}
              </Button>
            ))}
          </div>
        </Form.Group>

        {/* Auto-Share Configuration */}
        <div className="border rounded p-3 mb-3">
          <Form.Check
            type="switch"
            id="auto-share-switch"
            label={
              <strong>
                <Settings size={14} className="me-1" />
                Enable Auto-Sharing for Future Resources
              </strong>
            }
            checked={enableAutoShare}
            onChange={(e) => setEnableAutoShare(e.target.checked)}
            disabled={sharingInProgress}
          />

          {enableAutoShare && (
            <div className="mt-3 ms-4 p-3 bg-light rounded">
              <Form.Group className="mb-3">
                <Form.Label><small className="text-muted">Auto-Share Resource Types</small></Form.Label>
                <div className="d-flex flex-wrap gap-1">
                  {RESOURCE_TYPES.map(type => (
                    <Button
                      key={type.value}
                      size="sm"
                      variant={autoShareResourceTypes.includes(type.value) ? 'info' : 'outline-info'}
                      onClick={() => handleTypeToggle(type.value, true)}
                      disabled={sharingInProgress}
                      className="d-flex align-items-center"
                    >
                      {type.icon}
                      <span className="ms-1">{type.label}</span>
                    </Button>
                  ))}
                </div>
              </Form.Group>

              <Form.Group>
                <Form.Label><small className="text-muted">Auto-Share Permission Level</small></Form.Label>
                <div className="d-flex gap-1">
                  {PERMISSION_LEVELS.map(perm => (
                    <Button
                      key={perm.value}
                      size="sm"
                      variant={autoSharePermission === perm.value ? 'info' : 'outline-info'}
                      onClick={() => setAutoSharePermission(perm.value)}
                      disabled={sharingInProgress}
                      className="flex-grow-1"
                      title={perm.description}
                    >
                      {perm.label}
                    </Button>
                  ))}
                </div>
              </Form.Group>

              {autoShareStatus && (
                <div className="mt-2 p-2 bg-info bg-opacity-10 rounded">
                  <small className="text-info">
                    Currently auto-sharing: {autoShareStatus.resource_types?.join(', ')} at {autoShareStatus.permission_level} level
                  </small>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="alert alert-info alert-sm mb-0">
          <AlertTriangle size={14} className="me-1" />
          <small>
            Only resources you own or have admin access to will be shared.
            Auto-sharing will automatically share new resources as you create them.
          </small>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button size="sm" variant="secondary" onClick={handleClose} disabled={sharingInProgress}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={handleShare}
          disabled={sharingInProgress || selectedTypes.length === 0}
        >
          {sharingInProgress ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Sharing...
            </>
          ) : (
            <>
              <Share2 size={14} className="me-1" />
              Share {selectedTypes.length} Resource Type{selectedTypes.length !== 1 ? 's' : ''}
              {enableAutoShare && ' & Enable Auto-Share'}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkShareModal;